import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useExplorerMessagingLimit } from "./useExplorerMessagingLimit";
import {
  getExplorerMessagingLimitMessageFromError,
  getExplorerMessagingLimitReachedMessage,
} from "../utils/explorerMessagingLimitCopy";
import { isExplorerMessagingWeeklyLimitError } from "../utils/explorerMessagingError";
import { ExplorerMessagingLimitModal } from "../components/Messages/ExplorerMessagingLimitModal";

const PAYWALL_SHOWN_SESSION_KEY = "smbf-explorer-messaging-paywall-shown";

type Options = {
  /** Show paywall once per browser session when the weekly limit is already exhausted. */
  autoShowPaywall?: boolean;
};

/** Explorer messaging hard paywall — same weekResetsUtc as the limit API. */
export function useExplorerMessagingLimitFeedback(options?: Options) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [modalOpen, setModalOpen] = useState(false);
  const {
    isExplorer,
    canSendMessage,
    used,
    limit,
    weekResetsUtc,
    refetchLimit,
  } = useExplorerMessagingLimit();

  const openPaywall = useCallback(
    (opts?: { toast?: boolean; error?: unknown }) => {
      refetchLimit();
      if (opts?.toast !== false) {
        const message = opts?.error
          ? getExplorerMessagingLimitMessageFromError(t, opts.error, locale, {
              weekResetsUtc,
              used,
              limit,
            })
          : getExplorerMessagingLimitReachedMessage(t, locale, {
              weekResetsUtc,
              used,
              limit,
            });
        toast.error(message);
      }
      setModalOpen(true);
    },
    [t, locale, weekResetsUtc, used, limit, refetchLimit]
  );

  const guardSendMessage = useCallback(() => {
    if (!isExplorer || canSendMessage) return false;
    openPaywall({ toast: false });
    return true;
  }, [isExplorer, canSendMessage, openPaywall]);

  const handleMessagingLimitError = useCallback(
    (error: unknown) => {
      if (isExplorerMessagingWeeklyLimitError(error)) {
        openPaywall({ toast: true, error });
        return true;
      }
      return false;
    },
    [openPaywall]
  );

  useEffect(() => {
    if (!options?.autoShowPaywall || !isExplorer || canSendMessage) {
      return;
    }
    if (sessionStorage.getItem(PAYWALL_SHOWN_SESSION_KEY) === "1") {
      return;
    }
    sessionStorage.setItem(PAYWALL_SHOWN_SESSION_KEY, "1");
    openPaywall({ toast: false });
  }, [options?.autoShowPaywall, isExplorer, canSendMessage, openPaywall]);

  const limitModal = (
    <ExplorerMessagingLimitModal
      show={modalOpen}
      onHide={() => setModalOpen(false)}
      weekResetsUtc={weekResetsUtc}
      used={used}
      limit={limit}
    />
  );

  return {
    isExplorer,
    canSendMessage,
    guardSendMessage,
    handleMessagingLimitError,
    openPaywall,
    limitModal,
    refetchLimit,
  };
}
